/**
 * Validador de CNPJ (numérico e alfanumérico)
 * Baseado nas especificações da Receita Federal do Brasil
 * Suporta CNPJ numérico (formato atual) e CNPJ alfanumérico (formato futuro)
 * 
 * Referência: https://www.gov.br/receitafederal/pt-br/centrais-de-conteudo/publicacoes/apresentacoes/outros-eventos/cnpj-alfanumerico.pdf
 */

/**
 * Remove caracteres especiais do CNPJ (pontos, barras, hífens)
 */
export const cleanCNPJ = (cnpj: string): string => {
  return cnpj.replace(/[.\-/]/g, '').toUpperCase();
};

/**
 * Aplica máscara ao CNPJ: XX.XXX.XXX/XXXX-XX
 */
export const formatCNPJ = (cnpj: string): string => {
  const cleaned = cleanCNPJ(cnpj);
  if (cleaned.length !== 14) return cnpj;
  
  return cleaned.replace(
    /^(\w{2})(\w{3})(\w{3})(\w{4})(\w{2})$/,
    '$1.$2.$3/$4-$5'
  );
};

/**
 * Obtém o valor numérico para cálculo do dígito verificador
 * Para números: retorna o próprio número
 * Para letras: retorna o valor ASCII (A=65, B=66, etc.)
 * Conforme especificação: usar valores ASCII para cálculo do DV
 */
const getCharValue = (char: string): number => {
  if (/[0-9]/.test(char)) {
    return parseInt(char, 10);
  }
  // Para letras, usar valor ASCII (A=65, B=66, etc.)
  return char.charCodeAt(0);
};

/**
 * Calcula o dígito verificador do CNPJ
 * Suporta tanto formato numérico quanto alfanumérico
 * Usa valores ASCII para letras conforme especificação da RFB
 */
const calculateDV = (cnpjBase: string, weights: number[]): number => {
  let sum = 0;
  
  for (let i = 0; i < cnpjBase.length && i < weights.length; i++) {
    const char = cnpjBase[i];
    const weight = weights[i];
    if (char && weight !== undefined) {
      const charValue = getCharValue(char);
      sum += charValue * weight;
    }
  }
  
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
};

/**
 * Valida CNPJ (numérico ou alfanumérico)
 * 
 * @param cnpj - CNPJ com ou sem formatação
 * @returns true se o CNPJ é válido, false caso contrário
 */
export const validateCNPJ = (cnpj: string): boolean => {
  if (!cnpj) return false;
  
  const cleaned = cleanCNPJ(cnpj);
  
  // CNPJ deve ter exatamente 14 caracteres
  if (cleaned.length !== 14) return false;
  
  // Todos os caracteres devem ser válidos (0-9, A-Z)
  if (!/^[0-9A-Z]{14}$/.test(cleaned)) return false;
  
  // Não pode ser sequência de caracteres iguais
  if (/^(\w)\1{13}$/.test(cleaned)) return false;
  
  // Extrair base e dígitos verificadores
  const base = cleaned.substring(0, 12);
  const dv1 = cleaned.substring(12, 13);
  const dv2 = cleaned.substring(13, 14);
  
  // Pesos para cálculo do primeiro dígito verificador
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  
  // Calcular primeiro dígito verificador
  const calculatedDV1 = calculateDV(base, weights1);
  const dv1Value = getCharValue(dv1);
  
  // Verificar primeiro dígito
  if (calculatedDV1 !== dv1Value) return false;
  
  // Pesos para cálculo do segundo dígito verificador
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  
  // Calcular segundo dígito verificador (incluindo o primeiro DV)
  const baseWithDV1 = base + dv1;
  const calculatedDV2 = calculateDV(baseWithDV1, weights2);
  const dv2Value = getCharValue(dv2);
  
  // Verificar segundo dígito
  return calculatedDV2 === dv2Value;
};

/**
 * Valida e formata CNPJ
 * 
 * @param cnpj - CNPJ com ou sem formatação
 * @returns CNPJ formatado se válido, string vazia se inválido
 */
export const validateAndFormatCNPJ = (cnpj: string): string => {
  if (!cnpj) return '';
  
  const cleaned = cleanCNPJ(cnpj);
  
  if (validateCNPJ(cleaned)) {
    return formatCNPJ(cleaned);
  }
  
  return '';
};

